import React from 'react';
import { clsx } from 'clsx';
import {
  NumericFilterCondition,
  NumericFilterOperator,
  DEFAULT_NUMERIC_CONDITION,
} from '@/types';

const OPERATOR_OPTIONS: { value: NumericFilterOperator; label: string }[] = [
  { value: 'between', label: 'Between' },
  { value: 'lessThan', label: 'Less than (<)' },
  { value: 'greaterThan', label: 'Greater than (>)' },
  { value: 'equals', label: 'Equals (=)' },
  { value: 'notEqual', label: 'Not equal (≠)' },
  { value: 'isEmpty', label: 'Is empty' },
  { value: 'isNotEmpty', label: 'Is not empty' },
];

const needsTwoValues = (op: NumericFilterOperator): boolean => op === 'between';
const needsNoValues = (op: NumericFilterOperator): boolean =>
  op === 'isEmpty' || op === 'isNotEmpty';

export interface ConditionFilterProps {
  label: string;
  filterId: string;
  condition: NumericFilterCondition;
  onChange: (condition: NumericFilterCondition) => void;
  onReset: () => void;
  valueRange: { min: number; max: number };
  step?: number;
  isFocused?: boolean;
  hideLabel?: boolean;
  hideZero: boolean;
  hideBlanks: boolean;
  onHideZeroChange: (value: boolean) => void;
  onHideBlanksChange: (value: boolean) => void;
}

export const ConditionFilter: React.FC<ConditionFilterProps> = ({
  label,
  condition,
  onChange,
  onReset,
  valueRange,
  step = 0.01,
  hideLabel = false,
  hideZero,
  hideBlanks,
  onHideZeroChange,
  onHideBlanksChange,
}) => {
  const op = condition?.operator ?? DEFAULT_NUMERIC_CONDITION.operator;
  const hasValue =
    condition.value1 !== null ||
    condition.value2 !== null ||
    op === 'isEmpty' ||
    op === 'isNotEmpty';
  const showTwo = needsTwoValues(op);
  const showNone = needsNoValues(op);
  const handleOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const operator = e.target.value as NumericFilterOperator;
    onChange({
      operator,
      value1: operator === 'between' ? condition.value1 : condition.value1,
      value2: operator === 'between' ? condition.value2 : null,
    });
  };
  const handleValue1Change = (value: number | null): void => {
    onChange({ ...condition, value1: value });
  };
  const handleValue2Change = (value: number | null): void => {
    onChange({ ...condition, value2: value });
  };
  const handleValue1Blur = (): void => {
    if (showTwo && condition.value1 !== null && condition.value2 !== null && condition.value1 > condition.value2) {
      onChange({ ...condition, value1: condition.value2 });
    }
  };
  const handleValue2Blur = (): void => {
    if (showTwo && condition.value1 !== null && condition.value2 !== null && condition.value2 < condition.value1) {
      onChange({ ...condition, value2: condition.value1 });
    }
  };
  return (
    <div className="flex flex-col gap-2">
      {!hideLabel && (
        <div className="flex gap-2 items-center">
          <h3 className="text-sm font-medium text-gray-700">{label}</h3>
          {hasValue && (
            <button
              type="button"
              onClick={onReset}
              className="text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              Reset to default
            </button>
          )}
        </div>
      )}
      <div className="flex flex-col gap-2">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Operator</label>
          <select
            value={op}
            onChange={handleOperatorChange}
            className="w-full pl-2 pr-6 py-2 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
          >
            {OPERATOR_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {!showNone && (
          <div className={showTwo ? 'grid grid-cols-2 gap-2' : ''}>
            <div>
              <label className="block text-xs text-gray-500 mb-1">
                {showTwo ? 'Min' : 'Value'}
              </label>
              <input
                type="number"
                step={step}
                value={condition.value1 ?? ''}
                onChange={(e) =>
                  handleValue1Change(
                    e.target.value ? parseFloat(e.target.value) : null
                  )
                }
                onBlur={handleValue1Blur}
                className={clsx(
                  'w-full px-2 py-2 text-sm border rounded focus:ring-blue-500 focus:border-blue-500',
                  condition.value1 !== null && condition.value1 !== undefined
                    ? 'border-blue-500 ring-1 ring-blue-500'
                    : 'border-gray-300'
                )}
                placeholder={String(valueRange.min)}
              />
            </div>
            {showTwo && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Max</label>
                <input
                  type="number"
                  step={step}
                  value={condition.value2 ?? ''}
                  onChange={(e) =>
                    handleValue2Change(
                      e.target.value ? parseFloat(e.target.value) : null
                    )
                  }
                  onBlur={handleValue2Blur}
                  className={clsx(
                    'w-full px-2 py-2 text-sm border rounded focus:ring-blue-500 focus:border-blue-500',
                    condition.value2 !== null && condition.value2 !== undefined
                      ? 'border-blue-500 ring-1 ring-blue-500'
                      : 'border-gray-300'
                  )}
                  placeholder={String(valueRange.max)}
                />
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex flex-row items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={hideZero}
            onChange={(e) => onHideZeroChange(e.target.checked)}
            className="h-[13px] w-[13px] flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Hide 0
        </label>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={hideBlanks}
            onChange={(e) => onHideBlanksChange(e.target.checked)}
            className="h-[13px] w-[13px] flex-shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          Hide blanks
        </label>
      </div>
    </div>
  );
};

